import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from 'utils/cn'
import { useAnchoredPopover } from '../hooks/useAnchoredPopover'

interface BoxTruckEquipmentSelectProps {
    /** Selected equipment labels (subset of `options`). Empty = all / no narrowing. */
    values: string[]
    options: readonly string[]
    onChange: (values: string[]) => void
    placeholder?: string
    /** Greyed + non-interactive — e.g. "Bid loads" mode, where equipment isn't sent, so an enabled control
     *  would read as broken. */
    disabled?: boolean
}

/**
 * Compact multi-select for the Box Truck equipment LABELS (Cargo Van, Sprinter, …). ds2-styled to match the
 * filter bar's Select (h-9, rounded-xl surface trigger; portaled rounded-3xl panel), but multi: toggling a
 * row keeps the panel open, and the first row clears the selection ("All equipment"). The picked labels drive
 * the server-side search filter (unified codes + Sylectus vehicle sizes), so this is not a client-only filter.
 * Popover mechanics come from the shared useAnchoredPopover hook (same as SearchForDriverSelect).
 */
export const BoxTruckEquipmentSelect = ({ values, options, onChange, placeholder = 'All equipment', disabled = false }: BoxTruckEquipmentSelectProps) => {
    const { open, anchorRect, containerRef, triggerRef, panelRef, togglePanel, panelPositionStyle } = useAnchoredPopover()

    const toggle = (label: string) => {
        onChange(values.includes(label) ? values.filter((v) => v !== label) : [...values, label])
    }

    const triggerLabel = values.length === 0 ? placeholder : values.length === 1 ? values[0] : `${values.length} selected`

    const Row = ({ label, checked, onClick }: { label: string; checked: boolean; onClick: () => void }) => (
        <button
            type="button"
            onClick={onClick}
            className="flex min-h-9 w-full cursor-pointer items-center gap-2.5 rounded-[20px] border-none bg-transparent px-3 py-1.5 text-left transition-colors duration-100 hover:bg-default"
        >
            <span
                className={cn(
                    'flex size-4 shrink-0 items-center justify-center rounded-[5px] border transition-colors',
                    checked ? 'border-accent bg-accent text-background' : 'border-border bg-surface'
                )}
            >
                {checked && <Check size={12} strokeWidth={2.5} />}
            </span>
            <span className="whitespace-nowrap text-sm font-medium text-foreground">{label}</span>
        </button>
    )

    return (
        <div ref={containerRef} className="relative w-full">
            <button
                ref={triggerRef}
                type="button"
                disabled={disabled}
                onClick={togglePanel}
                className={cn(
                    'relative flex h-9 w-full items-center overflow-hidden rounded-xl border-none bg-surface p-0 text-left shadow-field outline-none transition-[box-shadow] duration-150 focus-visible:ring-2 focus-visible:ring-accent',
                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                )}
            >
                <div className="flex-1 overflow-hidden pl-3">
                    <span className={cn('whitespace-nowrap text-sm leading-5', values.length ? 'text-foreground' : 'text-foreground-muted')}>
                        {triggerLabel}
                    </span>
                </div>
                <span className="flex h-9 shrink-0 items-center justify-center px-3 py-2 text-foreground">
                    <ChevronDown size={16} strokeWidth={1.5} className={cn('transition-transform duration-200', open ? 'rotate-180' : 'rotate-0')} />
                </span>
            </button>

            {open &&
                anchorRect &&
                createPortal(
                    <div
                        ref={panelRef}
                        style={{ ...panelPositionStyle, minWidth: 200, maxHeight: Math.min(320, window.innerHeight - 16), overflowY: 'auto' }}
                        className="flex flex-col gap-0.5 rounded-3xl bg-surface p-1 shadow-overlay backdrop-blur-[20px]"
                    >
                        <Row label="All equipment" checked={values.length === 0} onClick={() => onChange([])} />
                        <div className="my-0.5 px-3">
                            <div className="h-px bg-border" />
                        </div>
                        {options.map((label) => (
                            <Row key={label} label={label} checked={values.includes(label)} onClick={() => toggle(label)} />
                        ))}
                    </div>,
                    document.body
                )}
        </div>
    )
}
